import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MyDataConnectLayout from '../components/mydata/MyDataConnectLayout';
import { getMyDataDraft, startMyDataAuthenticationAttempt } from '../utils/myDataDraft';
import { nhisLogin, nhisLoginMe, parseNhisError } from '../utils/nhisApi';
import { isAppEnvironment } from '../utils/auth';

import kakaoIcon from '../assets/icons/providers/kakao.png';
import passIcon from '../assets/icons/providers/pass.png';
import naverIcon from '../assets/icons/providers/naver.png';
import tossIcon from '../assets/icons/providers/toss.png';

const PROVIDERS = [
  { id: 'KAKAO', label: '카카오', color: '#FEE500', textColor: '#3C1E1E', icon: kakaoIcon },
  { id: 'PASS', label: 'PASS', color: '#ED1C24', textColor: '#fff', icon: passIcon },
  { id: 'NAVER', label: '네이버', color: '#03C75A', textColor: '#fff', icon: naverIcon },
  { id: 'TOSS', label: '토스', color: '#0064FF', textColor: '#fff', icon: tossIcon },
];

const CONSENT_ITEMS = [
  {
    key: 'thirdParty',
    label: '개인정보 제3자 제공 동의',
    required: true,
    detail:
      '주식회사 큐브드엠은 본인 확인 및 AI 건강 분석을 위해 아래와 같이 개인정보를 제3자에게 제공합니다.\n\n' +
      '[간편인증]\n' +
      '• 제공받는 자: 이용자가 선택한 간편인증 사업자 (카카오, PASS, 네이버, 토스)\n' +
      '• 이용 목적: 본인 확인을 위한 간편인증\n' +
      '• 제공 항목: 이름, 생년월일, 휴대폰번호\n' +
      '• 보유·이용 기간: 본인인증 후 즉시 파기\n\n' +
      '[AI 건강 분석]\n' +
      '• 제공받는 자: Google LLC (Google Cloud Vertex AI)\n' +
      '• 데이터 처리 리전: 한국 리전 (asia-northeast3)\n' +
      '• 이용 목적: 건강검진 결과의 AI 분석을 통한 맞춤형 건강 리포트 생성\n' +
      '• 제공 항목: 건강검진결과 데이터\n' +
      '• 보유·이용 기간: 분석 완료 후 즉시 삭제 (ZDR 정책, AI 모델 학습 미사용)\n\n' +
      '동의를 거부할 권리가 있으나, 거부 시 서비스 이용이 제한됩니다.',
  },
  {
    key: 'healthData',
    label: '건강(민감)정보 수집·이용 동의',
    required: true,
    detail:
      '주식회사 큐브드엠은 맞춤형 건강 리포트 제공을 위해 아래와 같이 건강(민감)정보를 수집·이용합니다.\n\n' +
      '• 수집 출처: 국민건강보험공단\n' +
      '• 수집·이용 목적: 맞춤형 건강 리포트 제공 및 AI 분석\n\n' +
      '[수집 항목]\n' +
      '① 건강검진결과\n' +
      '  - 계측: 신장, 체중, 허리둘레, 체질량지수(BMI)\n' +
      '  - 혈압: 수축기 혈압, 이완기 혈압\n' +
      '  - 혈액: 공복혈당, 총콜레스테롤, HDL콜레스테롤, LDL콜레스테롤, 중성지방,\n' +
      '          혈색소(빈혈), AST(SGOT), ALT(SGPT), 감마지티피(γ-GTP),\n' +
      '          신사구체여과율(GFR), 혈청크레아티닌\n' +
      '  - 요검사: 요단백\n' +
      '  - 기타: 시력(좌/우), 청력(좌/우), 골다공증, 폐결핵·흉부질환\n' +
      '  - 검진일자, 검진기관명, 소견\n' +
      '② 진료내역\n' +
      '  - 진료기관명, 진료일, 진료유형(외래·치과·처방조제), 처방건수\n' +
      '③ 투약정보\n' +
      '  - 약품명, 약효분류, 처방일, 투약일수\n' +
      '④ 암검진 결과\n' +
      '  - 암종류, 검진일, 검진기관명, 검진결과\n\n' +
      '• 보유·이용 기간: 회원 탈퇴 시까지\n\n' +
      '동의를 거부할 권리가 있으나, 거부 시 건강 리포트 서비스 이용이 제한됩니다.',
  },
];

function validateName(v) {
  return (v || '').trim().length >= 1 ? '' : '이름을 입력해주세요.';
}
function validateBirth(v) {
  if (!/^\d{8}$/.test(v)) return '생년월일 8자리를 입력해주세요.';
  const y = +v.slice(0, 4), m = +v.slice(4, 6), d = +v.slice(6, 8);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return '유효한 날짜를 입력해주세요.';
  return '';
}
function validatePhone(v) {
  return /^\d{10,11}$/.test(v) ? '' : '휴대폰번호 10~11자리를 입력해주세요.';
}

export default function MyDataConsentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/app') ? '/app' : '';
  const isApp = isAppEnvironment();

  const [provider, setProvider] = useState('');
  const [name, setName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [phone, setPhone] = useState('');
  const [consents, setConsents] = useState({ all: false, thirdParty: false, healthData: false });
  const [errors, setErrors] = useState({});
  const [expandedConsent, setExpandedConsent] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | error | timeout
  const [apiError, setApiError] = useState('');

  // Restore from draft
  useEffect(() => {
    const draft = getMyDataDraft();
    if (draft) {
      if (draft.provider) setProvider(draft.provider);
      if (!isApp && draft.name) setName(draft.name);
      if (!isApp && draft.birthdate) setBirthdate(draft.birthdate);
      if (!isApp && draft.phone) setPhone(draft.phone);
      if (draft.consents) setConsents(draft.consents);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConsentChange = (key) => {
    if (key === 'all') {
      const next = !consents.all;
      setConsents({ all: next, thirdParty: next, healthData: next });
    } else {
      const next = { ...consents, [key]: !consents[key] };
      next.all = next.thirdParty && next.healthData;
      setConsents(next);
    }
  };

  const handleBlur = (field) => {
    const validators = { name: validateName, birthdate: validateBirth, phone: validatePhone };
    const values = { name, birthdate, phone };
    const err = validators[field](values[field]);
    setErrors((prev) => ({ ...prev, [field]: err }));
  };

  const isLoading = status === 'loading';
  const canSubmit = isApp
    ? !isLoading && provider && consents.thirdParty && consents.healthData
    : !isLoading && provider && !validateName(name) && !validateBirth(birthdate) && !validatePhone(phone) && consents.thirdParty && consents.healthData;

  const handleSubmit = async () => {
    if (!provider || !consents.thirdParty || !consents.healthData) return;

    if (!isApp) {
      const errs = { name: validateName(name), birthdate: validateBirth(birthdate), phone: validatePhone(phone) };
      setErrors(errs);
      if (Object.values(errs).some(Boolean)) return;
    }

    setStatus('loading');
    setApiError('');
    try {
      let data;
      if (isApp) {
        data = await nhisLoginMe({ loginMethod: provider });
      } else {
        data = await nhisLogin({
          loginMethod: provider,
          name: name.trim(),
          phoneNumber: phone,
          birthDate: birthdate,
        });
      }

      startMyDataAuthenticationAttempt({
        phase: 'consent',
        provider,
        ...(isApp ? {} : { name: name.trim(), birthdate, phone }),
        consents,
        consentedAt: new Date().toISOString(),
        nhisSession: data.session,
        // 새로운 인증 시도 시 이전 환자 식별값이 남지 않도록 초기화
        stdPatientId: null,
      });
      navigate(basePath + '/mydata/connect/guide', { replace: true });
    } catch (err) {
      const parsed = parseNhisError(err);
      if (parsed.type === 'timeout') {
        setStatus('timeout');
      } else {
        setStatus('error');
      }
      setApiError(parsed.message);
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '11px 12px',
    fontSize: 15,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#fff',
    color: '#111827',
    boxSizing: 'border-box',
  };

  const tone = {
    surface: '#ffffff',
    surfaceMuted: '#f9fafb',
    border: '#e5e7eb',
    borderSoft: '#d1d5db',
    textStrong: '#111827',
    text: '#374151',
    textMuted: '#6b7280',
    primary: '#3498db',
    primarySoft: '#eff6ff',
  };

  return (
    <MyDataConnectLayout step={0}>
      {basePath !== '/app' && (
        <h1 className="page-title" style={{ textAlign: 'center', marginBottom: 16 }}>간편인증으로 본인 확인</h1>
      )}

      {/* 1단계 안내 박스 */}
      <div style={{
        background: tone.surface, border: `1px solid ${tone.border}`, borderRadius: 12,
        padding: '16px 18px', marginBottom: 24,
      }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: tone.textStrong, marginBottom: 12 }}>
          1단계 본인 인증
        </p>
        <p style={{ margin: 0, fontSize: 13, color: tone.text, lineHeight: 1.6 }}>
          인증 서비스를 선택하고 앱에서 본인 인증을 승인해 주세요.
        </p>
        <p style={{ margin: '4px 0 10px', fontSize: 12, color: tone.textMuted, lineHeight: 1.6 }}>
          인증 완료 후 다음 단계로 이동하며, 보통 1분 내외가 소요됩니다.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['건강검진결과', '진료내역', '투약정보'].map((item) => (
            <span key={item} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 20,
              background: tone.surfaceMuted, color: tone.text, fontWeight: 600,
              border: `1px solid ${tone.border}`,
            }}>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* Provider selection + Consents */}
      <div style={{ marginBottom: 24, border: `1px solid ${tone.border}`, borderRadius: 12, padding: 16, background: tone.surface }}>
        <p style={{ fontWeight: 700, marginBottom: 6, color: tone.textStrong, fontSize: 14 }}>인증 서비스 선택</p>
        <p style={{ fontSize: 13, color: tone.textMuted, marginBottom: 12, lineHeight: 1.6 }}>
          {isApp
            ? '본인 인증에 사용할 서비스를 선택해 주세요.'
            : '인증 서비스를 선택한 뒤 본인 정보를 입력해 주세요.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {PROVIDERS.map((p) => {
            const selected = provider === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setProvider(p.id)}
                aria-pressed={selected}
                aria-label={`${p.label} 간편인증 선택`}
                style={{
                  padding: '14px 10px',
                  borderRadius: 10,
                  fontWeight: 600,
                  fontSize: 13,
                  border: selected ? `2px solid ${tone.primary}` : `1px solid ${tone.border}`,
                  background: selected ? tone.primarySoft : tone.surface,
                  color: tone.textStrong,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <img src={p.icon} alt={p.label} style={{ width: 28, height: 28, objectFit: 'contain' }} />
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>

        {/* 동의 항목 */}
        <div style={{ marginTop: 16, borderTop: `1px solid ${tone.border}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CONSENT_ITEMS.map((item) => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              {/* 커스텀 체크 버튼 */}
              <button
                type="button"
                onClick={() => handleConsentChange(item.key)}
                aria-pressed={consents[item.key]}
                aria-label={`${item.label} ${consents[item.key] ? '선택됨' : '선택 안 됨'}`}
                style={{
                  flexShrink: 0,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: consents[item.key] ? 'none' : `2px solid #d1d5db`,
                  background: consents[item.key] ? tone.primary : '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  transition: 'background 0.15s, border 0.15s',
                }}
              >
                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                  <path d="M1 5l3.5 3.5L11 1" stroke={consents[item.key] ? '#fff' : '#d1d5db'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {/* 텍스트 */}
              <span
                onClick={() => handleConsentChange(item.key)}
                style={{ flex: 1, minWidth: 0, fontSize: 14, color: tone.text, wordBreak: 'keep-all', lineHeight: 1.5, cursor: 'pointer' }}
              >
                <span style={{ color: tone.primary, fontWeight: 700, marginRight: 4 }}>[필수]</span>
                {item.label}
              </span>
              {/* 상세 보기 */}
              <button
                type="button"
                onClick={() => setExpandedConsent(item.key)}
                aria-label={`${item.label} 상세 보기`}
                style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: tone.textMuted, fontSize: 18, lineHeight: 1, padding: '0 2px' }}
              >
                ›
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Form fields — 웹 모드에서만 표시 */}
      {!isApp && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20, border: `1px solid ${tone.border}`, borderRadius: 12, padding: 16, background: tone.surface }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: tone.textStrong }}>본인 정보 입력</p>
          <div>
            <label htmlFor="mydata-name" style={{ fontWeight: 600, fontSize: 14, color: tone.text }}>이름</label>
            <input id="mydata-name" name="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'mydata-name-error' : undefined}
              onBlur={() => handleBlur('name')} placeholder="홍길동" style={fieldStyle} />
            {errors.name && <div id="mydata-name-error" role="alert" aria-live="polite" style={{ color: '#e74c3c', fontSize: 12, marginTop: 4 }}>{errors.name}</div>}
          </div>
          <div>
            <label htmlFor="mydata-birth" style={{ fontWeight: 600, fontSize: 14, color: tone.text }}>생년월일</label>
            <input id="mydata-birth" name="birthdate" type="text" value={birthdate} onChange={(e) => setBirthdate(e.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric" autoComplete="bday" aria-invalid={Boolean(errors.birthdate)} aria-describedby={errors.birthdate ? 'mydata-birth-error' : undefined}
              onBlur={() => handleBlur('birthdate')} placeholder="19900101" maxLength={8} style={fieldStyle} />
            {errors.birthdate && <div id="mydata-birth-error" role="alert" aria-live="polite" style={{ color: '#e74c3c', fontSize: 12, marginTop: 4 }}>{errors.birthdate}</div>}
          </div>
          <div>
            <label htmlFor="mydata-phone" style={{ fontWeight: 600, fontSize: 14, color: tone.text }}>휴대폰번호</label>
            <input id="mydata-phone" name="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              inputMode="tel" autoComplete="tel" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'mydata-phone-error' : undefined}
              onBlur={() => handleBlur('phone')} placeholder="01012345678" maxLength={11} style={fieldStyle} />
            {errors.phone && <div id="mydata-phone-error" role="alert" aria-live="polite" style={{ color: '#e74c3c', fontSize: 12, marginTop: 4 }}>{errors.phone}</div>}
          </div>
        </div>
      )}


      {/* 바텀시트 */}
      {expandedConsent && (() => {
        const item = CONSENT_ITEMS.find((c) => c.key === expandedConsent);
        if (!item) return null;
        return (
          <>
            <style>{`
              @keyframes consent-sheet-up {
                from { transform: translateY(100%); }
                to   { transform: translateY(0); }
              }
              @keyframes consent-overlay-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
            `}</style>
            {/* 오버레이 */}
            <div
              onClick={() => setExpandedConsent(null)}
              style={{
                position: 'fixed', inset: 0, zIndex: 3000,
                background: 'rgba(0,0,0,0.45)',
                animation: 'consent-overlay-in 0.2s ease',
              }}
            />
            {/* 시트 */}
            <div style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 3001,
              background: '#fff',
              borderRadius: '20px 20px 0 0',
              maxHeight: '75vh',
              display: 'flex',
              flexDirection: 'column',
              animation: 'consent-sheet-up 0.28s cubic-bezier(0.32,0.72,0,1)',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
            }}>
              {/* 핸들 */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
              </div>
              {/* 헤더 */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 20px 12px',
                borderBottom: '1px solid #f3f4f6',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{item.label}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    border: '1px solid #bfdbfe', borderRadius: 12, padding: '2px 7px',
                    color: '#2563eb', background: '#eff6ff',
                  }}>필수</span>
                </div>
                <button
                  type="button"
                  onClick={() => setExpandedConsent(null)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#6b7280', fontSize: 22, lineHeight: 1, padding: 4,
                  }}
                >
                  ×
                </button>
              </div>
              {/* 본문 */}
              <div style={{
                overflowY: 'auto',
                padding: '16px 20px 32px',
                fontSize: 13,
                color: '#374151',
                lineHeight: 1.8,
                whiteSpace: 'pre-line',
              }}>
                {item.detail}
              </div>
            </div>
          </>
        );
      })()}

      {/* API 오류 */}
      {apiError && (
        <div role="alert" style={{
          marginBottom: 12, padding: '12px 14px', borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13, lineHeight: 1.5,
        }}>
          {apiError}
        </div>
      )}

      {/* CTA */}
      <button type="button" className="submit-button" disabled={!canSubmit} onClick={handleSubmit}
        style={{ width: '100%', padding: '14px 0', fontSize: 16, opacity: canSubmit ? 1 : 0.5 }}>
        동의하고 인증하기
      </button>

      {/* 로딩 오버레이 */}
      {isLoading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 20,
        }}>
          <style>{`@keyframes nhis-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{
            width: 52, height: 52,
            border: '5px solid rgba(255,255,255,0.3)',
            borderTop: '5px solid #fff',
            borderRadius: '50%',
            animation: 'nhis-spin 0.9s linear infinite',
          }} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#fff', fontSize: 16, fontWeight: 700, margin: 0, marginBottom: 6 }}>
              본인 인증을 요청하고 있습니다
            </p>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: 0 }}>
              잠시만 기다려 주세요.
            </p>
          </div>
        </div>
      )}
    </MyDataConnectLayout>
  );
}
